import Axios from "axios"
import urls from "./urls"
import {
  getAuthInfo,
  getUserLanguage,
  saveAuthInfo,
  clearAuthInfo
} from "./helpers"

const paramsSerializer = params => {
  const paramsList = []

  for (const [key, value] of Object.entries(params)) {
    if (value === "" || value === null) {
      continue
    }

    if (Array.isArray(value)) {
      value.forEach(v => paramsList.push(`${key}=${v}`))
    } else {
      paramsList.push(`${key}=${value}`)
    }
  }

  return paramsList.join("&")
}

const axios = Axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  withCredentials: true,
  paramsSerializer
})

const authorizationIsEmpty = request => {
  /**
   * If we send null in Authorization header from where the API is being called, axios transforms it into an empty object `{}`
   * That is why we are checking for object length and not null
   */
  return (
    request.headers["Authorization"] != null &&
    Object.keys(request.headers["Authorization"]).length === 0
  )
}

// request interceptor to add the token
axios.interceptors.request.use(request => {
  const authInfo = getAuthInfo()

  /**
   * Empty "Authorization" header means that we don't want to send this Authorization in the request even if accessToken is present in the localStorage
   * Example: in refresh token API call, we explicitly send Authorization: null from the API call.
   */
  if (authorizationIsEmpty(request)) {
    delete request.headers["Authorization"]
  } else {
    if (authInfo) {
      const tokenString = `Bearer ${authInfo.accessToken}`
      request.headers["Authorization"] = tokenString
    }
  }

  request.headers["Language"] = getUserLanguage()

  return request
})

let tokenIsRefreshing = false

const interceptor = error => {
  if (error.response) {
    if (error.response.status === 401) {
      if (tokenIsRefreshing) {
        return Promise.reject(error)
      }

      tokenIsRefreshing = true
      refreshToken()

      // Reject the current request while the token refreshes
      return Promise.reject(error)
    } else {
      return Promise.reject(error)
    }
  }
  return Promise.reject(error)
}

// Reference: https://gist.github.com/Godofbrowser/bf118322301af3fc334437c683887c5f
function refreshToken() {
  Axios({
    method: "post",
    url: `${process.env.REACT_APP_API_ENDPOINT}${urls.authentication.refreshToken}`,
    data: {
      refresh_token: getAuthInfo().refreshToken
    }
  })
    .then(response => {
      const authInfo = {
        ...getAuthInfo(),
        accessToken: response.data.data.access_token,
        refreshToken: response.data.data.refresh_token
      }
      saveAuthInfo(authInfo)

      // If refresh token call succeeds then resolve all the calls in the queue with the new token
      // processQueue(null, response.data.data.access_token)
      // resolve(axios(originalRequest))
      window.location.reload()
    })
    .catch(error => {
      if (error.response) {
        if (error.response.data.errors.refresh_token_expired) {
          // eslint-disable-next-line no-console
          console.log("> Refresh token expired. Logging out.")
          clearAuthInfo()
          window.location.reload()
        }
      }
      // If refresh token call fails then reject all the calls in the queue
      // processQueue(err)
      // reject(err)
    })
    .finally(() => {
      tokenIsRefreshing = false
    })
}

axios.interceptors.response.use(undefined, interceptor)

export default axios
